/**
 * Pure state machines for the enemy-mechanic overlays (no three.js, no allocation per frame).
 *
 * - `FadeTracker`: fixed-capacity keyed slots that fade in when a mark appears, stay live while
 *   it is touched each frame and fade out (optionally with a "resolved" flash) once it is gone.
 * - Small look functions that turn live mechanic numbers (soakers inside, time left, tension,
 *   absorb left) into what the shaders draw. Tested in mechanics.test.ts.
 *
 * Effects-intensity rule: information (rims, pips, countdown sweeps, tether lines) never drops
 * below `DANGER_FLOOR` of its alpha, so turning effects down cannot hide a mechanic; only motion
 * and flourish (flow speed, shimmer, shatter flash) scale fully with the setting.
 */

/** Minimum alpha factor for mechanic information at the lowest effects intensity. */
export const DANGER_FLOOR = 0.65;

/** Alpha factor for mechanic information at effects intensity `fx` (0.2..1.5 in settings). */
export function infoAlpha(fx: number): number {
  return Math.min(1, Math.max(DANGER_FLOOR, 0.55 + 0.45 * fx));
}

/** Multiplier for decorative motion / flourish at effects intensity `fx`. */
export function flourish(fx: number): number {
  return Math.min(1.2, Math.max(0, fx));
}

// ---- keyed fade slots --------------------------------------------------------------------

export const SlotState = { Free: 0, Entering: 1, Live: 2, Leaving: 3 } as const;
export type SlotState = (typeof SlotState)[keyof typeof SlotState];

export interface FadeSlot {
  key: string;
  state: SlotState;
  /** 0..1 visibility (fade in / fade out). */
  alpha: number;
  /** Seconds since the slot became live (entering). */
  age: number;
  /** Seconds since leaving began. */
  leaveAge: number;
  /** Touched this frame. */
  seen: boolean;
  /** Set by the caller before release: the mark went off (flash instead of a plain fade). */
  resolved: boolean;
  /** Free scratch for the owner (peak absorb, last tension...). */
  a: number;
  b: number;
}

export class FadeTracker {
  readonly slots: FadeSlot[] = [];
  private readonly index = new Map<string, number>();

  constructor(
    readonly capacity: number,
    readonly inS = 0.15,
    readonly outS = 0.25,
  ) {
    for (let i = 0; i < capacity; i++) this.slots.push({ key: '', state: SlotState.Free, alpha: 0, age: 0, leaveAge: 0, seen: false, resolved: false, a: 0, b: 0 });
  }

  /** Start a frame: every slot is unseen until touched. */
  begin(): void {
    for (const s of this.slots) s.seen = false;
  }

  /**
   * Mark `key` present this frame. Returns its slot index, or -1 when every slot is busy (the
   * mark is skipped; leaving slots are reclaimed first, oldest-leaving first).
   */
  touch(key: string): number {
    let i = this.index.get(key);
    if (i !== undefined) {
      const s = this.slots[i]!;
      if (s.state === SlotState.Leaving) {
        // Came back (re-applied debuff, re-cast): fade back in from where it is.
        s.state = SlotState.Entering;
        s.resolved = false;
      }
      s.seen = true;
      return i;
    }
    i = this.claim();
    if (i < 0) return -1;
    const s = this.slots[i]!;
    if (s.key) this.index.delete(s.key);
    s.key = key;
    s.state = SlotState.Entering;
    s.alpha = 0;
    s.age = 0;
    s.leaveAge = 0;
    s.seen = true;
    s.resolved = false;
    s.a = 0;
    s.b = 0;
    this.index.set(key, i);
    return i;
  }

  private claim(): number {
    let leaving = -1;
    let leavingAge = -1;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i]!;
      if (s.state === SlotState.Free) return i;
      if (s.state === SlotState.Leaving && s.leaveAge > leavingAge) {
        leaving = i;
        leavingAge = s.leaveAge;
      }
    }
    return leaving;
  }

  /** Slots not touched since `begin()` start leaving; ages and alphas advance by `dt` seconds. */
  end(dt: number): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i]!;
      if (s.state === SlotState.Free) continue;
      if (!s.seen && s.state !== SlotState.Leaving) {
        s.state = SlotState.Leaving;
        s.leaveAge = 0;
      }
      if (s.state === SlotState.Leaving) {
        s.leaveAge += dt;
        const out = s.resolved ? Math.max(this.outS, RESOLVE_FLASH_S) : this.outS;
        s.alpha = Math.max(0, 1 - s.leaveAge / out);
        if (s.leaveAge >= out) {
          this.index.delete(s.key);
          s.key = '';
          s.state = SlotState.Free;
          s.alpha = 0;
        }
        continue;
      }
      s.age += dt;
      if (s.state === SlotState.Entering) {
        s.alpha = Math.min(1, s.alpha + dt / Math.max(1e-3, this.inS));
        if (s.alpha >= 1) s.state = SlotState.Live;
      }
    }
  }

  /** Number of non-free slots (tests / debug). */
  active(): number {
    let n = 0;
    for (const s of this.slots) if (s.state !== SlotState.Free) n++;
    return n;
  }

  clear(): void {
    for (const s of this.slots) {
      s.state = SlotState.Free;
      s.key = '';
      s.alpha = 0;
    }
    this.index.clear();
  }
}

/** Duration of the "it went off" flash on resolved telegraph marks (waves, soaks, snaps). */
export const RESOLVE_FLASH_S = 0.35;

// ---- soak --------------------------------------------------------------------------------

export interface SoakLook {
  /** Pips drawn (needed count, capped). */
  pips: number;
  /** Filled pips (eligible soakers inside, capped at pips). */
  filled: number;
  satisfied: boolean;
  /** 0 = calm gold rim, 1 = urgent red pulsing rim (short of soakers close to resolving). */
  urgency: number;
}

export const MAX_SOAK_PIPS = 5;

export function soakLook(need: number, inside: number, progress: number, out: SoakLook): SoakLook {
  const pips = Math.max(1, Math.min(MAX_SOAK_PIPS, Math.round(need)));
  out.pips = pips;
  out.filled = Math.max(0, Math.min(pips, Math.round(inside)));
  out.satisfied = inside >= need;
  // Short of soakers: the rim warms toward red over the second half of the timer.
  out.urgency = out.satisfied ? 0 : Math.min(1, Math.max(0, (progress - 0.45) / 0.4));
  return out;
}

// ---- explode / spread countdown ------------------------------------------------------------

export interface CountdownLook {
  /** Remaining fraction of the debuff (sweep arc), 1 → 0. */
  sweep: number;
  /** Rim pulse frequency (Hz): calm → fast in the last seconds. */
  pulseHz: number;
  /** Last window: rim and marker go deadly red. */
  urgent: boolean;
}

/** The final window where the ring turns red and pulses fast (≥ the 1.5 s reaction floor). */
export const URGENT_MS = 1500;

export function countdownLook(remainingMs: number, durationMs: number, out: CountdownLook): CountdownLook {
  const total = Math.max(1, durationMs);
  const left = Math.max(0, remainingMs);
  out.sweep = Math.min(1, left / total);
  out.urgent = left <= URGENT_MS;
  out.pulseHz = left <= URGENT_MS ? 4 : left <= 3000 ? 2 : 0.7;
  return out;
}

/** Ring radius for `growsToExpiry` hazards: half size at application, full at expiry. */
export function growingRadius(radius: number, sweep: number, grows: boolean): number {
  return grows ? radius * (0.5 + 0.5 * (1 - sweep)) : radius;
}

// ---- tethers -----------------------------------------------------------------------------

export interface TetherLook {
  /** Strip width in yards. */
  width: number;
  /** 0 = slack (tether red), 1 = about to snap (pale hot core). */
  strain: number;
}

export function tetherLook(tension: number, fixate: boolean, out: TetherLook): TetherLook {
  if (fixate) {
    out.width = 0.22;
    out.strain = 0;
    return out;
  }
  const t = Math.max(0, Math.min(1, tension));
  out.strain = t;
  // Thicker (tighter) as it stretches toward breakAt, so "keep running" reads at a glance.
  out.width = 0.22 + 0.2 * t;
  return out;
}

// ---- waves -------------------------------------------------------------------------------

/** A band starts arming (sweeping front) this long before it goes off. */
export const WAVE_ARM_MS = 1500;

export const WavePhase = { Queued: 0, Armed: 1, Fired: 2 } as const;
export type WavePhase = (typeof WavePhase)[keyof typeof WavePhase];

export function wavePhase(resolvesInMs: number, isNext: boolean): WavePhase {
  return isNext || resolvesInMs <= WAVE_ARM_MS ? WavePhase.Armed : WavePhase.Queued;
}

/** Front position across the band, 0 (near edge) → 1 (far edge) as it nears resolution. */
export function waveFront(resolvesInMs: number): number {
  return Math.min(1, Math.max(0, 1 - resolvesInMs / WAVE_ARM_MS));
}

// ---- shielded cast bubble ----------------------------------------------------------------

export const BubbleState = { Gone: 0, Intact: 1, Cracking: 2, Shattered: 3 } as const;
export type BubbleState = (typeof BubbleState)[keyof typeof BubbleState];

export const SHATTER_S = 0.45;

export interface Bubble {
  state: BubbleState;
  /** Highest absorb seen this application (the shield's size). */
  peak: number;
  /** 0 = whole, 1 = about to break. */
  crack: number;
  /** Seconds in the current state (shatter flash timing). */
  t: number;
}

export function newBubble(): Bubble {
  return { state: BubbleState.Gone, peak: 0, crack: 0, t: 0 };
}

/**
 * Advance a bubble. `absorb` is the live absorb left, or null when the shield aura is gone.
 * Intact → Cracking once a quarter of it is chipped; Cracking/Intact → Shattered when the aura
 * drops (broken or dispelled); a shield that simply expires untouched fades without shattering.
 */
export function stepBubble(b: Bubble, absorb: number | null, dt: number): Bubble {
  b.t += dt;
  if (absorb === null) {
    if (b.state === BubbleState.Intact || b.state === BubbleState.Cracking) {
      if (b.crack > 0.05) {
        b.state = BubbleState.Shattered;
        b.t = 0;
      } else {
        b.state = BubbleState.Gone;
        b.peak = 0;
        b.crack = 0;
      }
    } else if (b.state === BubbleState.Shattered && b.t >= SHATTER_S) {
      b.state = BubbleState.Gone;
      b.peak = 0;
      b.crack = 0;
    }
    return b;
  }
  if (b.state === BubbleState.Gone || b.state === BubbleState.Shattered || absorb > b.peak * 1.05) {
    // New (or refreshed) shield.
    b.state = BubbleState.Intact;
    b.peak = Math.max(1, absorb);
    b.t = 0;
  }
  b.peak = Math.max(b.peak, absorb);
  b.crack = Math.min(1, Math.max(0, 1 - absorb / b.peak));
  const next = b.crack >= 0.25 ? BubbleState.Cracking : BubbleState.Intact;
  if (next !== b.state) {
    b.state = next;
    b.t = 0;
  }
  return b;
}

/** Shatter flash 1 → 0 over SHATTER_S (0 outside Shattered). */
export function shatterFlash(b: Bubble): number {
  return b.state === BubbleState.Shattered ? Math.max(0, 1 - b.t / SHATTER_S) : 0;
}
